<template>
  <div class="crud_form_wrapper">
    <!-- Start:: Title -->
    <div class="form_title_wrapper">
      <h4>{{ $t("PLACEHOLDERS.stable_data") }}</h4>
    </div>
    <div class="col-12 text-end">
      <v-btn @click="$router.go(-1)" style="color: #F7941E">
        <i class="fas fa-backward"></i>
      </v-btn>
    </div>
    <!-- End:: Title -->

    <!-- Start:: Single Step Form Content -->
    <div class="single_step_form_content_wrapper">
      <form @submit.prevent="validateFormInputs">
        <!-- Start:: Basic Information -->
        <div class="row">
          <div class="col-12 mb-3">
            <h5 class="section-title">{{ $t("PLACEHOLDERS.basic_info") }}</h5>
          </div>
          
          <!-- Start:: Avatar -->
          <div v-if="data.avatar" class="col-12 mb-3">
            <div class="preview-container text-center">
              <img :src="data.avatar" :alt="data.name" class="stable-avatar" />
              <p class="mt-2 text-muted">{{ $t('PLACEHOLDERS.stable_image') }}</p>
            </div>
          </div>
          <!-- End:: Avatar -->

          <!-- Start:: Stable Name -->
          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.stable_name')"
            v-model.trim="data.name"
            disabled
          />
          <!-- End:: Stable Name -->

          <!-- Start:: Full Mobile -->
          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.phone')"
            v-model.trim="data.full_mobile"
            disabled
          />
          <!-- End:: Full Mobile -->

          <!-- Start:: Email -->
          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.email')"
            v-model.trim="data.email"
            disabled
          />
          <!-- End:: Email -->

          <!-- Start:: City -->
          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.city')"
            v-model.trim="data.city"
            disabled
          />
          <!-- End:: City -->

          <!-- Start:: Commercial Registration -->
          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.commercial_registration_number')"
            v-model.trim="data.commercial_registration_number"
            disabled
          />
          <!-- End:: Commercial Registration -->

          <!-- Start:: Description (if exists) -->
          <base-input
            v-if="data.description"
            col="12"
            type="textarea"
            :placeholder="$t('PLACEHOLDERS.description')"
            v-model.trim="data.description"
            disabled
          />
          <!-- End:: Description -->

          <!-- Start:: Location Map -->
          <div v-if="data.latitude && data.longitude" class="col-12 mt-4">
            <h5 class="section-title">{{ $t("PLACEHOLDERS.location") }}</h5>
            <div class="map_wrapper">
              <GmapMap
                :center="{
                  lat: parseFloat(data.latitude),
                  lng: parseFloat(data.longitude),
                }"
                :zoom="13"
                map-type-id="terrain"
                style="width: 100%; height: 500px"
              >
                <GmapMarker
                  :position="{
                    lat: parseFloat(data.latitude),
                    lng: parseFloat(data.longitude),
                  }"
                  :clickable="false"
                  :draggable="false"
                />
              </GmapMap>
            </div>
          </div>
          <!-- End:: Location Map -->

          <!-- Start:: Stable Images -->
          <div v-if="hasStableImages" class="col-12 mt-4">
            <h5 class="section-title">{{ $t("PLACEHOLDERS.stable_images") }}</h5>
            <div class="images-container">
              <div 
                v-for="(image, index) in stableImages" 
                :key="index"
                class="image-wrapper"
              >
                <img 
                  :src="image.url || image" 
                  :alt="`Stable Image ${index + 1}`"
                  class="stable-image"
                  @click="openImageModal(image.url || image)"
                />
              </div>
            </div>
          </div>
          <!-- End:: Stable Images -->
        </div>
        <!-- End:: Basic Information -->
      </form>
    </div>
    <!-- END:: Single Step Form Content -->

    <!-- Start:: Image Modal -->
    <v-dialog v-model="imageModal" max-width="800px">
      <v-card v-if="selectedImage">
        <v-card-title>{{ $t("PLACEHOLDERS.stable_image") }}</v-card-title>
        <v-card-text>
          <img :src="selectedImage" alt="" style="width: 100%; height: auto;" />
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="imageModal = false">{{ $t("BUTTONS.close") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- End:: Image Modal -->
  </div>
</template>

<script>
export default {
  name: "ShowStable",

  computed: {
    hasStableImages() {
      return this.stableImages.length > 0;
    },

    stableImages() {
      // Check if stable_data exists and has images
      if (this.data.images && this.data.images.length > 0) {
        return this.data.images;
      }
      return [];
    },
  },

  data() {
    return {
      // Start:: Loader Control Data
      isWaitingRequest: false,
      // End:: Loader Control Data

      // Start:: Data Collection
      data: {
        id: null,
        name: null,
        mobile: null,
        full_mobile: null,
        email: null,
        city: null,
        district: null,
        location: null,
        latitude: null,
        longitude: null,
        avatar: null,
        commercial_registration_number: null,
        description: null,
        address: null,
        images: [],
        is_active: null,
        created_at: null,
        updated_at: null,
      },
      // End:: Data Collection

      // Modals
      imageModal: false,
      selectedImage: null,
    };
  },

  methods: {
    // Start:: Open Image Modal
    openImageModal(imageUrl) {
      this.selectedImage = imageUrl;
      this.imageModal = true;
    },
    // End:: Open Image Modal

    // Start:: Get Data To Show
    async getDataToShow() {
      this.isWaitingRequest = true;
      try {
        let res = await this.$axios({
          method: "GET",
          url: `stables/${this.$route.params.id}`,
        });

        // Start:: Set Data from API response
        const stableData = res.data.data.stable;

        this.data.id = stableData.id;
        this.data.name = stableData.name || this.$t("TABLES.noData");
        this.data.mobile = stableData.mobile || this.$t("TABLES.noData");
        this.data.full_mobile = stableData.full_mobile || stableData.mobile || this.$t("TABLES.noData");
        this.data.email = stableData.email || this.$t("TABLES.noData");
        this.data.city = stableData.city || this.$t("TABLES.noData");
        this.data.district = stableData.district || '';
        this.data.location = stableData.location || '';
        this.data.latitude = stableData.latitude;
        this.data.longitude = stableData.longitude;
        this.data.avatar = stableData.avatar;
        this.data.commercial_registration_number = stableData.commercial_registration_number || this.$t("TABLES.noData");
        this.data.description = stableData.description || '';
        this.data.address = stableData.address || '';
        this.data.images = stableData.stable_data?.images || [];
        this.data.is_active = stableData.is_active;
        this.data.created_at = stableData.created_at;
        this.data.updated_at = stableData.updated_at;
        // End:: Set Data

        this.isWaitingRequest = false;
      } catch (error) {
        this.isWaitingRequest = false;
        console.log(error.response?.data?.message || "Error loading stable data");
        this.$message.error(this.$t("MESSAGES.somethingWentWrong"));
        this.$router.push({ path: "/stables/all" });
      }
    },
    // End:: Get Data To Show
  },

  async created() {
    // Start:: Fire Methods
    await this.getDataToShow();
    // End:: Fire Methods
  },
};
</script>

<style scoped>
.preview-container {
  margin-bottom: 20px;
}

.preview-container img {
  max-width: 100%;
  max-height: 200px;
  border: 1px solid #ccc;
  border-radius: 15px;
}

.stable-avatar {
  max-width: 200px;
  max-height: 200px;
  border: 3px solid #ddd;
  border-radius: 50%;
  object-fit: cover;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.section-title {
  color: #2c3e50;
  border-bottom: 2px solid #3498db;
  padding-bottom: 8px;
  margin-bottom: 20px;
  font-weight: bold;
}

.map_wrapper {
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid #eee;
  margin-top: 10px;
}

.images-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 15px;
  padding: 10px;
  background-color: #f8f9fa;
  border-radius: 8px;
  margin-top: 5px;
}

.image-wrapper {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
}

.image-wrapper:hover {
  transform: scale(1.05);
  cursor: pointer;
}

.stable-image {
  width: 100%;
  height: 150px;
  object-fit: cover;
  border-radius: 8px;
  transition: opacity 0.3s ease;
}

.stable-image:hover {
  opacity: 0.9;
}

@media (max-width: 768px) {
  .images-container {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 10px;
  }
  
  .stable-image {
    height: 120px;
  }
  
  .stable-avatar {
    max-width: 150px;
    max-height: 150px;
  }
}
</style>
